'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { Button } from 'components/atomic/nuclei';
import { HeaderOrganism } from 'components/atomic/organism';
import { mediaStoreBaseURL, staticMediaStoreBaseURL } from 'config/common';
import { useIsMobile } from 'helpers/screenResolutionHelper';
import { getPublishedCollections } from 'services/collection.service';
import { getPageURL, ROUTE_CONFIG } from 'services/hostPortal.service';

const CollectionCardLinkList = ({ collectionData }) =>
  collectionData.map(({ slug, id, bannerImageUrl, name }) => {
    const collectionHomePageURL = getPageURL({
      pageName: ROUTE_CONFIG.COLLECTION_HOME.label,
      pathParams: {
        collectionSlug: slug
      }
    });

    return (
      <Link
        href={collectionHomePageURL}
        key={id}
      >
        <div className='relative group cursor-pointer'>
          <div className='w-full'>
            <Image
              height={400}
              width={460}
              className='object-contain w-full rounded-t-lg transition duration-300 ease-in-out'
              src={`${mediaStoreBaseURL}/${bannerImageUrl}`}
              alt='banner'
            />
          </div>

          <div className='bg-white pb-3 px-3 pt-2 rounded-b-lg shadow-category'>
            <span className='font-medium text-sm leading-5 text-nero'>
              {name}
            </span>
          </div>

          <div
            className={`absolute top-0 bg-black bg-opacity-70 opacity-0 rounded-lg w-full h-full flex items-center group-hover:opacity-100 transition ease-in-out duration-700`}
          >
            <div className='max-w-40 md:max-w-53 mx-auto w-full'>
              <Link href={collectionHomePageURL}>
                <Button
                  className={`block text-center bg-transparent h-10 md:h-12.5 py-2.5 md:py-3.5 rounded-md text-cinnabar text-sm md:text-base font-medium leading-snug md:leading-tight border border-cinnabar transition-opacity delay-1000 ease-in-out duration-1000`}
                >
                  View Collection
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Link>
    );
  });

export default function CollectionDetails({ collections }) {
  const [isMobile] = useIsMobile();
  const [collectionData, setCollectionData] = useState(collections);
  const [isLoading, setIsLoading] = useState(false);
  const [bannerUrl, setBannerUrl] = useState(
    `'${staticMediaStoreBaseURL}/banners/collection-banner.jpg'`
  );
  const [pagination, setPagination] = useState({
    start: 0,
    end: 10
  });

  useEffect(() => {
    setPagination({
      start: 0,
      end: 10
    });

    async function fetchActiveCollections() {
      const collection = await getPublishedCollections(0, 10);
      setCollectionData(collection.entity);
    }
    fetchActiveCollections();
  }, [collections]);

  useEffect(() => {
    const mobileStub = isMobile ? '/mobile-banner' : '';
    setBannerUrl(
      `'${staticMediaStoreBaseURL}${mobileStub}/banners/collection-banner.jpg'`
    );
  }, [isMobile]);

  const fetchData = async () => {
    setIsLoading(true);

    const { start } = pagination;
    const data = await getPublishedCollections(
      pagination.start + 10,
      pagination.end
    );

    const fetchedCollections = data.entity;
    const totalCollectionLoaded = collectionData.concat(fetchedCollections);

    setCollectionData(totalCollectionLoaded);
    setPagination({ start: start + 10, end: 10 });
    setIsLoading(false);
  };

  return (
    <>
      <HeaderOrganism heading='Collections' />
      <div
        className='relative w-full bg-cover bg-left-top bg-no-repeat h-40 md:h-64 flex justify-center items-center'
        style={{
          backgroundImage: `url(${bannerUrl})`
        }}
      >
        <div className='text-center w-full flex flex-col'>
          <span className='border-b border-white max-w-30 md:max-w-85 mx-auto'></span>
          <div className='px-5 max-w-xs md:max-w-165 text-center mx-auto'>
            <div className='text-3xl md:text-5xl font-brandon uppercase leading-7 md:leading-12.5 text-nero'>
              {isMobile
                ? 'OUR CURATED COLLECTIONS'
                : 'Explore hand-picked Hafla collections curated just for you!'}
            </div>
          </div>
        </div>
      </div>

      {collectionData?.length ? (
        <InfiniteScroll
          dataLength={collectionData.length}
          next={fetchData}
          hasMore={true}
          loader={isLoading ? <h4>Loading...</h4> : ''}
        >
          <div className='container mx-auto w-full p-5 md:px-0 md:py-6'>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-y-5 md:gap-10'>
              {collectionData?.length ? (
                <CollectionCardLinkList {...{ collectionData }} />
              ) : (
                ''
              )}
            </div>
          </div>
        </InfiniteScroll>
      ) : (
        ''
      )}
    </>
  );
}
